import React from 'react'
import { Calendar } from '../types/dimmingProfile.types'
import { DimmingProfileUiStrings } from '../i18n/dimmingProfileI18n'
import { levelForZenith, solarZenithDeg, sunHorizonTimes } from '../utils/solar'

interface DailyStepsCurveProps {
  calendar: Calendar
  strings: DimmingProfileUiStrings
}

// Light palette + geometry shared with CalendarCurve so the diagrams read as a
// set (see CalendarCurve.tsx).
const COLOR_AXIS = '#9ca3af'
const COLOR_GRID = '#eef2f7'
const COLOR_GRID_MAJOR = '#dde3ec'
const COLOR_CURVE = '#2563eb'
const COLOR_MARKER = '#f59e0b'

const LEVEL_TICKS = [0, 25, 50, 75, 100]

const WIDTH = 640
const HEIGHT = 240
const MARGIN_LEFT = 40
const MARGIN_RIGHT = 16
const MARGIN_TOP = 28
const MARGIN_BOTTOM = 36
const PLOT_WIDTH = WIDTH - MARGIN_LEFT - MARGIN_RIGHT
const PLOT_HEIGHT = HEIGHT - MARGIN_TOP - MARGIN_BOTTOM

// Vertical inset of the LEVEL scale inside the plot: 0% plateaus used to lie
// exactly ON the bottom border (invisible). Mirrors CalendarCurve /
// CalendarStepsPresenter so the calendar charts read as a set.
const LEVEL_INSET_TOP = 8
const LEVEL_INSET_BOTTOM = 12
const LEVEL_SPAN = PLOT_HEIGHT - LEVEL_INSET_TOP - LEVEL_INSET_BOTTOM

// Padding (hours) added on each side of the [sunrise, sunset] window so the
// markers and edge plateaus are not flush against the plot border.
const PAD_HOURS = 0.25
// One sample per minute across the daylight window.
const SAMPLE_MINUTES = 1

const levelToY = (level: number): number =>
  MARGIN_TOP + LEVEL_INSET_TOP + (1 - level / 100) * LEVEL_SPAN

// Format decimal solar hours (e.g. 6.5) as 2-digit HH:MM.
function formatHHMM(tHours: number): string {
  const total = Math.round(tHours * 60)
  const h = Math.floor(total / 60)
  const m = total % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

// True when the curve cannot be drawn (missing latitude). With a latitude but no
// steps the curve still renders as a flat 0% staircase ("toujours afficher
// l'aperçu").
function isCurveUnavailable(calendar: Calendar): boolean {
  return !calendar.latitude_deg
}

// Read-only intensity-vs-time preview ZOOMED to the daylight window
// [sunrise, sunset], emphasising the dimming plateaus (paliers) as a true
// staircase. See utils/solar.ts for the (equinox) model's limits.
const DailyStepsCurve: React.FC<DailyStepsCurveProps> = ({
  calendar,
  strings,
}) => {
  const horizon = isCurveUnavailable(calendar)
    ? null
    : sunHorizonTimes(calendar.latitude_deg)

  if (!horizon) {
    return (
      <p style={{ fontSize: 12, color: '#6b7280', margin: '6px 2px 0' }}>
        {strings.calendarCurveNoData}
      </p>
    )
  }

  const lat = calendar.latitude_deg
  const tStart = horizon.sunrise - PAD_HOURS
  const tEnd = horizon.sunset + PAD_HOURS
  const span = tEnd - tStart

  // Degenerate window guard (avoids divide-by-zero / NaN in path d).
  if (span <= 0) {
    return (
      <p style={{ fontSize: 12, color: '#6b7280', margin: '6px 2px 0' }}>
        {strings.calendarCurveNoData}
      </p>
    )
  }

  const timeToX = (tHours: number): number =>
    MARGIN_LEFT + ((tHours - tStart) / span) * PLOT_WIDTH

  // Sample minute-by-minute across the window. Morning (t < 12) uses
  // sunrise_steps, afternoon/evening uses sunset_steps (mirrors CalendarCurve).
  const sampleCount = Math.max(1, Math.round((span * 60) / SAMPLE_MINUTES))
  const samples: Array<{ t: number; level: number }> = []
  for (let i = 0; i <= sampleCount; i++) {
    const t = tStart + (i / sampleCount) * span
    const steps = t < 12 ? calendar.sunrise_steps : calendar.sunset_steps
    const level = levelForZenith(solarZenithDeg(lat, t), steps)
    samples.push({ t, level })
  }

  // Build a TRUE STAIRCASE path: when the level changes between two samples,
  // draw the horizontal segment to the transition x at the OLD level, then a
  // vertical segment to the NEW level. Plateaus stay flat, transitions vertical.
  const segs: string[] = []
  {
    const first = samples[0]
    segs.push(`M ${timeToX(first.t).toFixed(2)} ${levelToY(first.level).toFixed(2)}`)
    for (let i = 1; i < samples.length; i++) {
      const prev = samples[i - 1]
      const cur = samples[i]
      const x = timeToX(cur.t)
      if (cur.level !== prev.level) {
        // Horizontal to the transition x at the previous level, then vertical.
        segs.push(`L ${x.toFixed(2)} ${levelToY(prev.level).toFixed(2)}`)
        segs.push(`L ${x.toFixed(2)} ${levelToY(cur.level).toFixed(2)}`)
      } else {
        segs.push(`L ${x.toFixed(2)} ${levelToY(cur.level).toFixed(2)}`)
      }
    }
  }
  const pathD = segs.join(' ')

  // Contiguous plateaus (one label each, centred on the plateau midpoint).
  // A plateau runs while the level is unchanged across consecutive samples.
  const plateaus: Array<{ tMid: number; level: number; widthPx: number }> = []
  {
    let runStart = 0
    for (let i = 1; i <= samples.length; i++) {
      const ended = i === samples.length || samples[i].level !== samples[runStart].level
      if (ended) {
        const tMid = (samples[runStart].t + samples[i - 1].t) / 2
        const widthPx = timeToX(samples[i - 1].t) - timeToX(samples[runStart].t)
        plateaus.push({ tMid, level: samples[runStart].level, widthPx })
        runStart = i
      }
    }
  }

  // Sunrise/sunset markers (inside the padded window).
  const markers: Array<{ t: number; label: string }> = [
    { t: horizon.sunrise, label: strings.sunrise },
    { t: horizon.sunset, label: strings.sunset },
  ]

  // X tick labels: sunrise, noon (if in range), sunset.
  const noonInRange = 12 >= tStart && 12 <= tEnd
  const xTicks: Array<{ t: number; major: boolean }> = [
    { t: horizon.sunrise, major: false },
    ...(noonInRange ? [{ t: 12, major: true }] : []),
    { t: horizon.sunset, major: false },
  ]

  return (
    <div style={{ width: '100%', maxWidth: 640 }}>
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        style={{
          width: '100%',
          height: 'auto',
          background: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: 6,
        }}
      >
        {/* Title */}
        <text
          x={MARGIN_LEFT}
          y={16}
          textAnchor="start"
          fontSize={13}
          fill="#374151"
        >
          {strings.dailyStepsTitle}
        </text>

        {/* Plot border */}
        <rect
          x={MARGIN_LEFT}
          y={MARGIN_TOP}
          width={PLOT_WIDTH}
          height={PLOT_HEIGHT}
          fill="none"
          stroke={COLOR_AXIS}
          strokeWidth={1}
        />

        {/* Horizontal level grid + labels */}
        {LEVEL_TICKS.map((lvl) => {
          const y = levelToY(lvl)
          return (
            <g key={`lvl-${lvl}`}>
              <line
                x1={MARGIN_LEFT}
                y1={y}
                x2={MARGIN_LEFT + PLOT_WIDTH}
                y2={y}
                stroke={lvl === 0 ? COLOR_GRID_MAJOR : COLOR_GRID}
                strokeWidth={1}
              />
              <text
                x={MARGIN_LEFT - 8}
                y={y + 4}
                textAnchor="end"
                fontSize={11}
                fill="#6b7280"
              >
                {lvl}
              </text>
            </g>
          )
        })}

        {/* Noon major gridline (if within the window) */}
        {noonInRange && (
          <line
            x1={timeToX(12)}
            y1={MARGIN_TOP}
            x2={timeToX(12)}
            y2={MARGIN_TOP + PLOT_HEIGHT}
            stroke={COLOR_GRID_MAJOR}
            strokeWidth={1}
          />
        )}

        {/* X tick labels (HH:MM) */}
        {xTicks.map((tick, idx) => (
          <text
            key={`xt-${idx}`}
            x={timeToX(tick.t)}
            y={MARGIN_TOP + PLOT_HEIGHT + 16}
            textAnchor="middle"
            fontSize={11}
            fill="#6b7280"
          >
            {formatHHMM(tick.t)}
          </text>
        ))}

        {/* Sunrise / sunset amber dashed markers */}
        {markers.map((m, idx) => {
          const x = timeToX(m.t)
          return (
            <g key={`marker-${idx}`}>
              <line
                x1={x}
                y1={MARGIN_TOP}
                x2={x}
                y2={MARGIN_TOP + PLOT_HEIGHT}
                stroke={COLOR_MARKER}
                strokeWidth={1}
                strokeDasharray="4 3"
              />
              <text
                x={x}
                y={MARGIN_TOP + 12}
                textAnchor="middle"
                fontSize={10}
                fill={COLOR_MARKER}
              >
                {m.label}
              </text>
            </g>
          )
        })}

        {/* Intensity staircase */}
        <path d={pathD} fill="none" stroke={COLOR_CURVE} strokeWidth={2} />

        {/* Plateau level labels (one per contiguous plateau, skip too-narrow) */}
        {plateaus.map((p, idx) =>
          p.widthPx >= 24 ? (
            <text
              key={`plat-${idx}`}
              x={timeToX(p.tMid)}
              y={levelToY(p.level) - 4}
              textAnchor="middle"
              fontSize={10}
              fill="#6b7280"
            >
              {`${p.level}%`}
            </text>
          ) : null,
        )}

        {/* Axis captions */}
        <text
          x={MARGIN_LEFT + PLOT_WIDTH / 2}
          y={HEIGHT - 2}
          textAnchor="middle"
          fontSize={12}
          fill="#374151"
        >
          {strings.timeAxis}
        </text>
        <text
          x={12}
          y={MARGIN_TOP + PLOT_HEIGHT / 2}
          textAnchor="middle"
          fontSize={12}
          fill="#374151"
          transform={`rotate(-90 12 ${MARGIN_TOP + PLOT_HEIGHT / 2})`}
        >
          {strings.levelAxis}
        </text>
      </svg>
      <p style={{ fontSize: 12, color: '#6b7280', margin: '6px 2px 0' }}>
        {strings.dailyStepsHint}
      </p>
    </div>
  )
}

export default DailyStepsCurve
